// Clock extrapolation: the engine sends `{sample_pos, tick_pos, ...}` about every 16 ms; every
// frame the UI estimates the *audible* musical position from the latest clock and performance.now().

export interface ClockEvent {
  samplePos: number;
  tickPos: number;
  transportTick: number;
  patternLength: number;
  bpm: number;
  sampleRate: number;
  playing: boolean;
  recording: boolean;
  latencySamples: number;
}

export interface AudiblePosition {
  /** Pattern-relative tick of what is being heard right now (straight time). */
  tick: number;
  /** Ticks since the pattern/song started (for 4-bar cheers). Negative during count-in. */
  transportTick: number;
  playing: boolean;
  countIn: boolean;
}

/**
 * Maps performance.now() to the engine sample counter with a filter that tracks the *earliest*
 * arrival (IPC delay is always positive): late events nudge the estimate slowly, early events
 * pull it forward quickly, and big jumps (device restart, first event) snap.
 */
export class ClockModel {
  private baseTime = 0;
  private basePos = 0;
  private rate = 48000;
  private last: ClockEvent | null = null;
  private lastArrival = 0;
  /** Most recent prediction error in ms (shown in the F1 overlay). */
  driftMs = 0;
  /** Smoothed |error| in ms. */
  jitterMs = 0;

  get latest(): ClockEvent | null {
    return this.last;
  }

  predictSample(nowMs: number): number {
    return this.basePos + ((nowMs - this.baseTime) * this.rate) / 1000;
  }

  onClock(ev: ClockEvent, nowMs: number): void {
    this.rate = ev.sampleRate;
    if (!this.last) {
      this.baseTime = nowMs;
      this.basePos = ev.samplePos;
    } else {
      const predicted = this.predictSample(nowMs);
      const err = ev.samplePos - predicted;
      this.driftMs = (err / this.rate) * 1000;
      this.jitterMs += (Math.abs(this.driftMs) - this.jitterMs) * 0.05;
      if (Math.abs(err) > this.rate * 0.1) {
        this.baseTime = nowMs;
        this.basePos = ev.samplePos;
      } else {
        // Re-base at now with a correction: fast when the engine is ahead, slow when behind.
        const alpha = err > 0 ? 0.5 : 0.03;
        this.basePos = predicted + err * alpha;
        this.baseTime = nowMs;
      }
    }
    this.last = ev;
    this.lastArrival = nowMs;
  }

  /** Has a clock arrived recently? */
  alive(nowMs: number): boolean {
    return this.last !== null && nowMs - this.lastArrival < 500;
  }

  audible(nowMs: number): AudiblePosition {
    const ev = this.last;
    if (!ev) return { tick: 0, transportTick: 0, playing: false, countIn: false };
    if (!ev.playing) return { tick: 0, transportTick: 0, playing: false, countIn: false };
    const audibleSample = this.predictSample(nowMs) - ev.latencySamples;
    const tps = (ev.bpm * 960) / (60 * ev.sampleRate);
    const dt = (audibleSample - ev.samplePos) * tps;
    let tick = ev.tickPos + dt;
    const transportTick = ev.transportTick + dt;
    const len = ev.patternLength;
    const countIn = transportTick < 0;
    if (!countIn && len > 0) {
      tick = ((tick % len) + len) % len;
    }
    return { tick, transportTick, playing: true, countIn };
  }
}
